import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { useNovu } from '@novu/react'
import type { Notification } from '@novu/react'
import { useNotificationCount } from './NotificationCountContext'

interface NotificationCacheContextValue {
  cachedNotifications: Notification[]
  addNotification: (notification: Notification) => void
  updateNotification: (id: string, updates: Partial<Notification>) => void
  removeNotification: (id: string) => void
  clearCache: () => void
  syncCache: (notifications: Notification[]) => void
}

const NotificationCacheContext = createContext<NotificationCacheContextValue | null>(null)

export function useNotificationCache() {
  const context = useContext(NotificationCacheContext)
  if (!context) {
    throw new Error('useNotificationCache must be used within NotificationCacheProvider')
  }
  return context
}

interface NotificationCacheProviderProps {
  children: ReactNode
}

export function NotificationCacheProvider({ children }: NotificationCacheProviderProps) {
  const novu = useNovu()
  const { refetch: refetchCount } = useNotificationCount()
  const [cachedNotifications, setCachedNotifications] = useState<Notification[]>([])

  // Add a notification to cache (from WebSocket)
  const addNotification = useCallback((notification: Notification) => {
    setCachedNotifications(prev => {
      const exists = prev.some(n => n.id === notification.id)
      if (exists) {
        return prev
      }
      return [notification, ...prev]
    })
  }, [])

  // Update a notification in cache (e.g., read/unread status)
  const updateNotification = useCallback((id: string, updates: Partial<Notification>) => {
    setCachedNotifications(prev => {
      return prev.map(n =>
        n.id === id
          // Novu 的 Notification 是带方法的 class，spread 得到的是 plain object——仅类型层面回标为 Notification
          ? ({ ...n, ...updates } as Notification) // Create new object with updates
          : n
      )
    })
  }, [])

  // Remove a notification from cache (e.g., archived)
  const removeNotification = useCallback((id: string) => {
    setCachedNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  // Clear all cached notifications
  const clearCache = useCallback(() => {
    setCachedNotifications([])
  }, [])

  // Sync cache with a list of notifications (merge and deduplicate)
  const syncCache = useCallback((notifications: Notification[]) => {
    setCachedNotifications(prev => {
      // Create a map of existing cached notifications by ID
      const existingMap = new Map(prev.map(n => [n.id, n]))

      // Add or update notifications from the sync list
      notifications.forEach(notification => {
        existingMap.set(notification.id, notification)
      })

      // Convert map back to array and sort by creation date
      const result = Array.from(existingMap.values())
      return result.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
    })
  }, [])


  // WebSocket event handlers
  useEffect(() => {
    if (!novu) return

    const handleNewNotification = (notification: Notification) => {
      addNotification(notification)
      // Debounced refetch count
      setTimeout(() => refetchCount(), 300)
    }

    const handleNotificationRead = (data: { id: string }) => {
      updateNotification(data.id, { isRead: true } as Partial<Notification>)
      setTimeout(() => refetchCount(), 300)
    }

    const handleNotificationUnread = (data: { id: string }) => {
      updateNotification(data.id, { isRead: false } as Partial<Notification>)
      setTimeout(() => refetchCount(), 300)
    }

    const handleNotificationArchived = (data: { id: string }) => {
      removeNotification(data.id)
      setTimeout(() => refetchCount(), 300)
    }

    // Subscribe to WebSocket events
    // @ts-ignore - Novu SDK event types may be incomplete
    novu.on('notification.received', handleNewNotification)
    // @ts-ignore
    novu.on('notification.read', handleNotificationRead)
    // @ts-ignore
    novu.on('notification.unread', handleNotificationUnread)
    // @ts-ignore
    novu.on('notification.archived', handleNotificationArchived)

    // Cleanup
    return () => {
      // @ts-ignore
      novu.off('notification.received', handleNewNotification)
      // @ts-ignore
      novu.off('notification.read', handleNotificationRead)
      // @ts-ignore
      novu.off('notification.unread', handleNotificationUnread)
      // @ts-ignore
      novu.off('notification.archived', handleNotificationArchived)
    }
  }, [novu, addNotification, updateNotification, removeNotification, refetchCount])

  const value = {
    cachedNotifications,
    addNotification,
    updateNotification,
    removeNotification,
    clearCache,
    syncCache
  }

  return (
    <NotificationCacheContext.Provider value={value}>
      {children}
    </NotificationCacheContext.Provider>
  )
}

// Fallback provider when Novu is not available
export function NotificationCacheProviderFallback({ children }: NotificationCacheProviderProps) {
  const value: NotificationCacheContextValue = {
    cachedNotifications: [],
    addNotification: () => {},
    updateNotification: () => {},
    removeNotification: () => {},
    clearCache: () => {},
    syncCache: () => {}
  }

  return (
    <NotificationCacheContext.Provider value={value}>
      {children}
    </NotificationCacheContext.Provider>
  )
}
